<template>
  <v-dialog :value="value" max-width="500" @input="onDialogInput">
    <div class="overpayment-list-popup">
      <strong class="overpayment-list-popup__title">과오납 내역 조회</strong>

      <div v-if="loading" class="overpayment-list-popup__loading">
        <v-progress-circular indeterminate color="#ef761d" size="28" />
        <p>내역을 불러오는 중입니다...</p>
      </div>

      <div v-else-if="!items || items.length === 0" class="overpayment-list-popup__empty">
        <p>과오납 내역이 없습니다.</p>
      </div>

      <div v-else class="overpayment-list-popup__list">
        <div
          v-for="item in items"
          :key="item.request_no"
          class="overpayment-item"
          :class="{ 'is-open': expandedId === item.request_no }"
        >
          <!-- 헤더: 항상 노출, 클릭 시 펼치기/접기 -->
          <button
            type="button"
            class="overpayment-item__header"
            @click="toggleExpand(item.request_no)"
          >
            <div class="overpayment-item__header-main">
              <span class="overpayment-item__description">
                {{ isRent ? item.receipt_type : item.yn_option }}
                <span class="overpayment-item__date">{{ formatDate(item.approval_date) }}</span>
              </span>
              <span class="overpayment-item__badge" :class="statusBadgeClass(item.status)">
                {{ item.status }}
              </span>
            </div>
            <div class="overpayment-item__header-sub">
              <span>{{ headerAmountLabel(item) }} <strong>{{ headerAmountValue(item) | comma }}원</strong></span>
            </div>
            <span class="overpayment-item__chevron" :class="{ 'is-open': expandedId === item.request_no }">
              ▾
            </span>
          </button>

          <!-- 상세: 펼쳤을 때만 노출 -->
          <div v-if="expandedId === item.request_no" class="overpayment-item__detail">
            <template v-if="item.is_enabled_request_refund">
              <div class="overpayment-item__detail-row">
                <span class="overpayment-item__detail-label">계약자명</span>
                <span class="overpayment-item__detail-value">{{ item.contractor_name }}</span>
              </div>
              <div class="overpayment-item__detail-row">
                <span class="overpayment-item__detail-label">발생일자</span>
                <span class="overpayment-item__detail-value">{{ formatDate(item.approval_date) }}</span>
              </div>
              <div class="overpayment-item__detail-row">
                <span class="overpayment-item__detail-label">출처구분</span>
                <span class="overpayment-item__detail-value">{{ item.refund_source }}</span>
              </div>
              <div class="overpayment-item__detail-row">
                <span class="overpayment-item__detail-label">발생총금액</span>
                <span class="overpayment-item__detail-value is-amount">{{ item.approval_amount | comma }}원</span>
              </div>
              <div class="overpayment-item__detail-row">
                <span class="overpayment-item__detail-label">환불가능금액</span>
                <span class="overpayment-item__detail-value is-amount">{{ item.refundable_amount | comma }}원</span>
              </div>
              <button
                v-if="item.is_enabled_request_refund"
                type="button"
                class="overpayment-item__refund-btn"
                @click="$emit('refund', item)"
              >
                환불 신청하기
              </button>
            </template>

            <template v-else>
              <div class="overpayment-item__detail-row">
                <span class="overpayment-item__detail-label">계약자명</span>
                <span class="overpayment-item__detail-value">{{ item.contractor_name }}</span>
              </div>
              <div class="overpayment-item__detail-row">
                <span class="overpayment-item__detail-label">발생일자</span>
                <span class="overpayment-item__detail-value">{{ formatDate(item.approval_date) }}</span>
              </div>
              <div class="overpayment-item__detail-row">
                <span class="overpayment-item__detail-label">출처구분</span>
                <span class="overpayment-item__detail-value">{{ item.refund_source }}</span>
              </div>
              <div class="overpayment-item__detail-row">
                <span class="overpayment-item__detail-label">발생총금액</span>
                <span class="overpayment-item__detail-value">{{ item.approval_amount | comma }}원</span>
              </div>
              <div class="overpayment-item__detail-row">
                <span class="overpayment-item__detail-label">환불금액</span>
                <span class="overpayment-item__detail-value is-amount">{{ item.refund_amount | comma }}원</span>
              </div>
              <div class="overpayment-item__detail-row">
                <span class="overpayment-item__detail-label">환불가능금액</span>
                <span class="overpayment-item__detail-value">{{ item.refundable_amount | comma }}원</span>
              </div>
              <div class="overpayment-item__detail-row">
                <span class="overpayment-item__detail-label">환불신청일자</span>
                <span class="overpayment-item__detail-value">{{ formatDate(item.refund_request_date) }}</span>
              </div>
              <div class="overpayment-item__detail-row">
                <span class="overpayment-item__detail-label">환불예금주</span>
                <span class="overpayment-item__detail-value">{{ item.refund_account_owner }}</span>
              </div>
              <div class="overpayment-item__detail-row">
                <span class="overpayment-item__detail-label">환불은행</span>
                <span class="overpayment-item__detail-value">{{ item.refund_bank_name }}</span>
              </div>
              <div class="overpayment-item__detail-row">
                <span class="overpayment-item__detail-label">환불계좌번호</span>
                <span class="overpayment-item__detail-value">{{ item.refund_account_no }}</span>
              </div>
              <div v-if="item.is_canceled" class="overpayment-item__detail-row">
                <span class="overpayment-item__detail-label">취소사유</span>
                <span class="overpayment-item__detail-value is-reject">{{ item.cancel_reason }}</span>
              </div>

              <template v-if="item.is_re_requestable">
                <div class="overpayment-item__detail-row">
                  <span class="overpayment-item__detail-label">재요청사유</span>
                  <span class="overpayment-item__detail-value is-reject">{{ item.reject_reason }}</span>
                </div>
                <button
                  type="button"
                  class="overpayment-item__refund-btn"
                  @click="$emit('refund', item)"
                >
                  환불 재신청하기
                </button>
              </template>
            </template>
          </div>
        </div>
      </div>

      <div class="overpayment-list-popup__notice">
        <p>※ 환불 신청 후 처리까지 영업일 기준 3~5일이 소요될 수 있습니다.</p>
        <p>※ 상태가 '완료'이더라도 실제 입금은 시간이 더 소요될 수 있습니다.</p>
        <p>※ 환불 관련 문의는 고객센터로 연락해주세요.</p>
      </div>

      <div class="overpayment-list-popup__footer">
        <button
          type="button"
          class="overpayment-list-popup__close"
          @click="$emit('input', false)"
        >
          닫기
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
export default {
  name: "OverpaymentListDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isRent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expandedId: null,
    };
  },
  watch: {
    value(val) {
      if (!val) this.expandedId = null;
    },
  },
  methods: {
    toggleExpand(id) {
      this.expandedId = this.expandedId === id ? null : id;
    },
    statusBadgeClass(status) {
      if (status === "완료") return "is-complete";
      if (status === "대상지정") return "is-available";
      if (status === "처리중") return "is-progress";
      if (status === "취소") return "is-cancel";
      return "";
    },
    headerAmountLabel(item) {
      return item.refund_request_date ? "환불금액" : "환불가능금액";
    },
    headerAmountValue(item) {
      return item.refund_request_date ? item.refund_amount : item.refundable_amount;
    },
    onDialogInput(val) {
      this.$emit("input", val);
    },
    formatDate(dt) {
      if (!dt || dt.length !== 8) return dt || "-";
      return `${dt.slice(0, 4)}-${dt.slice(4, 6)}-${dt.slice(6, 8)}`;
    },
  },
};
</script>

<style scoped>
.overpayment-list-popup {
  border-radius: 20px;
  background-color: #ffffff;
  padding: 28px 24px 22px;
}

.overpayment-list-popup__title {
  display: block;
  margin-bottom: 24px;
  text-align: center;
  color: #e66a1f;
  font-size: 24px;
  font-weight: 700;
}

.overpayment-list-popup__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 0;
}

.overpayment-list-popup__loading p {
  margin: 0;
  color: #444444;
  font-size: 14px;
}

.overpayment-list-popup__empty {
  padding: 32px 0;
  text-align: center;
  color: #7d7d7d;
  font-size: 14px;
}

.overpayment-list-popup__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}

/* 아이템 카드 */
.overpayment-item {
  border: 1px solid #252525;
  border-radius: 12px;
  overflow: hidden;
  background-color: #ffffff;
}

/* 헤더 버튼 */
.overpayment-item__header {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  background-color: #ffffff;
  border: 0;
  text-align: left;
  cursor: pointer;
  position: relative;
}

.overpayment-item__header-main {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 24px;
}

.overpayment-item__description {
  flex: 1 1 auto;
  color: #232323;
  font-size: 14px;
  font-weight: 700;
}

.overpayment-item__date {
  margin-left: 6px;
  color: #8c8c8c;
  font-size: 12px;
  font-weight: 400;
}

.overpayment-item__badge {
  flex: 0 0 auto;
  padding: 3px 10px;
  border-radius: 999px;
  background-color: #d9d9d9;
  color: #232323;
  font-size: 12px;
  font-weight: 700;
}

.overpayment-item__badge.is-complete {
  background-color: #e66a1f;
  color: #ffffff;
}

.overpayment-item__badge.is-available {
  background-color: #e8f1ff;
  color: #1a7ef7;
}

.overpayment-item__badge.is-progress {
  background-color: #00181a;
  color: #ffffff;
}

.overpayment-item__badge.is-cancel {
  color: #ff0000;
}

.overpayment-item__header-sub {
  display: flex;
  gap: 14px;
  color: #8c8c8c;
  font-size: 12px;
}

.overpayment-item__header-sub strong {
  color: #232323;
  font-weight: 700;
}

.overpayment-item__chevron {
  position: absolute;
  right: 14px;
  top: 14px;
  color: #ef761d;
  font-size: 16px;
  line-height: 1;
  transition: transform 0.2s;
  display: inline-block;
}

.overpayment-item__chevron.is-open {
  transform: rotate(180deg);
}

/* 상세 영역 */
.overpayment-item__detail {
  padding: 14px 16px 16px;
  border-top: 1px solid #e5e5e5;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.overpayment-item__detail-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.overpayment-item__detail-label {
  flex: 0 0 90px;
  color: #b8b8b8;
  font-size: 13px;
  font-weight: 700;
}

.overpayment-item__detail-value {
  color: #232323;
  font-size: 13px;
}

.overpayment-item__detail-value.is-amount {
  color: #ef761d;
  font-weight: 700;
}

.overpayment-item__detail-value.is-reject {
  color: #e53935;
}

.overpayment-item__refund-btn {
  margin-top: 6px;
  width: 100%;
  height: 42px;
  border: 0;
  border-radius: 999px;
  background-color: #ef761d;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
}

.overpayment-list-popup__notice {
  margin-bottom: 20px;
}

.overpayment-list-popup__notice p {
  margin: 0 0 6px;
  text-align: left;
  color: #ef761d;
  font-size: 12px;
  line-height: 1.5;
  word-break: keep-all;
}

.overpayment-list-popup__notice p:last-child {
  margin-bottom: 0;
}

.overpayment-list-popup__footer {
  display: flex;
  justify-content: flex-end;
}

.overpayment-list-popup__close {
  min-width: 78px;
  height: 40px;
  border: 0;
  border-radius: 999px;
  background-color: #d9d9d9;
  color: #232323;
  font-size: 14px;
  font-weight: 700;
}

@media (max-width: 359px) {
  .overpayment-list-popup {
    padding: 24px 18px 18px;
  }

  .overpayment-item__header-sub {
    flex-direction: column;
    gap: 4px;
  }
}
</style>
